"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Restricted markdown renderer — raw HTML disabled (react-markdown default),
// user links open in new tabs with nofollow/ugc, no images inside bodies
// (post images are a dedicated field), headers downscaled to keep density.
export function ForumMarkdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={`forum-markdown text-sm leading-relaxed break-words ${className || ""}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        allowedElements={[
          "p", "br", "strong", "em", "del", "a", "ul", "ol", "li",
          "blockquote", "code", "pre", "h1", "h2", "h3", "h4", "hr", "table",
          "thead", "tbody", "tr", "th", "td",
        ]}
        unwrapDisallowed
        components={{
          a: ({ href, children: kids }) => (
            <a
              href={href}
              target="_blank"
              rel="nofollow ugc noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:opacity-80"
              onClick={(e) => e.stopPropagation()}
            >
              {kids}
            </a>
          ),
          h1: ({ children: kids }) => <h3 className="text-base font-semibold mt-3 mb-1">{kids}</h3>,
          h2: ({ children: kids }) => <h3 className="text-base font-semibold mt-3 mb-1">{kids}</h3>,
          h3: ({ children: kids }) => <h4 className="text-sm font-semibold mt-2 mb-1">{kids}</h4>,
          h4: ({ children: kids }) => <h4 className="text-sm font-semibold mt-2 mb-1">{kids}</h4>,
          ul: ({ children: kids }) => <ul className="list-disc pl-5 my-1.5 space-y-0.5">{kids}</ul>,
          ol: ({ children: kids }) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5">{kids}</ol>,
          blockquote: ({ children: kids }) => (
            <blockquote className="border-l-2 border-primary/40 pl-3 my-2 text-muted-foreground italic">{kids}</blockquote>
          ),
          code: ({ className: cls, children: kids }) =>
            cls?.includes("language-") ? (
              <code className="block bg-muted rounded-md p-2 my-2 text-xs overflow-x-auto font-mono">{kids}</code>
            ) : (
              <code className="bg-muted rounded px-1 py-0.5 text-xs font-mono">{kids}</code>
            ),
          pre: ({ children: kids }) => <pre className="my-0">{kids}</pre>,
          p: ({ children: kids }) => <p className="my-1.5 first:mt-0 last:mb-0">{kids}</p>,
          table: ({ children: kids }) => (
            <div className="overflow-x-auto my-2">
              <table className="text-xs border-collapse [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_td]:border [&_td]:px-2 [&_td]:py-1">{kids}</table>
            </div>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
